import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Send,
  MessageSquare,
  Users,
  TrendingUp,
  Plus,
  Zap,
  Bot,
  Radio,
  AlertCircle,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatNumber, formatPercentage } from "@/lib/utils";
import type { DashboardStats, ChartDataPoint, ActivityEvent } from "@/types";

// Mock data
const mockStats: DashboardStats = {
  dispatches_today: 127,
  messages_sent: 3842,
  active_groups: 48,
  success_rate: 97.3,
};

const mockChartData: ChartDataPoint[] = [
  { date: "Seg", dispatches: 45, messages: 1200 },
  { date: "Ter", dispatches: 52, messages: 1450 },
  { date: "Qua", dispatches: 38, messages: 980 },
  { date: "Qui", dispatches: 65, messages: 1800 },
  { date: "Sex", dispatches: 78, messages: 2100 },
  { date: "Sab", dispatches: 92, messages: 2800 },
  { date: "Dom", dispatches: 127, messages: 3842 },
];

const mockActivity: ActivityEvent[] = [
  {
    id: "1",
    type: "dispatch",
    title: "Dispatch concluido",
    description: "Promo Black Friday enviada para 24 grupos",
    created_at: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
  },
  {
    id: "2",
    type: "agent_response",
    title: "Agente respondeu",
    description: "3 mensagens respondidas no grupo 'Vendas SP'",
    created_at: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
  },
  {
    id: "3",
    type: "channel_connected",
    title: "Canal conectado",
    description: "WhatsApp 'Ofertas RJ' reconectado",
    created_at: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
  },
  {
    id: "4",
    type: "error",
    title: "Erro no dispatch",
    description: "Falha ao enviar para 'Telegram Promo' - timeout",
    created_at: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
  },
];

function useCountUp(end: number, duration: number = 1500): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number | null = null;
    let animationId: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      setCount(Math.round(eased * end));

      if (progress < 1) {
        animationId = requestAnimationFrame(animate);
      }
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [end, duration]);

  return count;
}

const statCards = [
  {
    label: "Dispatches Hoje",
    key: "dispatches_today" as const,
    icon: Send,
    color: "text-primary",
    bg: "bg-primary/10",
  },
  {
    label: "Msgs Enviadas",
    key: "messages_sent" as const,
    icon: MessageSquare,
    color: "text-emerald-400",
    bg: "bg-emerald-400/10",
  },
  {
    label: "Grupos Ativos",
    key: "active_groups" as const,
    icon: Users,
    color: "text-sky-400",
    bg: "bg-sky-400/10",
  },
  {
    label: "Taxa Sucesso",
    key: "success_rate" as const,
    icon: TrendingUp,
    color: "text-amber-400",
    bg: "bg-amber-400/10",
  },
];

const activityIcons: Record<string, { icon: typeof Zap; color: string }> = {
  dispatch: { icon: Zap, color: "text-primary" },
  agent_response: { icon: Bot, color: "text-emerald-400" },
  channel_connected: { icon: Radio, color: "text-sky-400" },
  error: { icon: AlertCircle, color: "text-rose-400" },
};

function StatCard({
  label,
  value,
  icon: Icon,
  color,
  bg,
  index,
}: {
  label: string;
  value: number;
  icon: typeof Send;
  color: string;
  bg: string;
  index: number;
}) {
  const animatedValue = useCountUp(value);
  const isPercentage = label.includes("Taxa");

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.3 }}
    >
      <Card className="relative overflow-hidden">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-muted-foreground">
                {label}
              </p>
              <p className="mt-2 text-2xl font-bold tracking-tight text-foreground">
                {isPercentage
                  ? formatPercentage(animatedValue)
                  : formatNumber(animatedValue)}
              </p>
            </div>
            <div
              className={`flex h-11 w-11 items-center justify-center rounded-xl ${bg}`}
            >
              <Icon className={`h-5 w-5 ${color}`} />
            </div>
          </div>
          {/* Subtle gradient accent */}
          <div
            className={`absolute bottom-0 left-0 h-[2px] w-full bg-gradient-to-r from-transparent ${color.replace("text-", "via-")} to-transparent opacity-50`}
          />
        </CardContent>
      </Card>
    </motion.div>
  );
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "agora";
  if (mins < 60) return `${mins}m atras`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h atras`;
  return `${Math.floor(hours / 24)}d atras`;
}

function CustomTooltipContent({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number; color: string }>; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-card p-3 shadow-xl">
      <p className="mb-1 text-xs font-medium text-muted-foreground">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} className="text-sm font-semibold" style={{ color: entry.color }}>
          {formatNumber(entry.value)}
        </p>
      ))}
    </div>
  );
}

export function HomePage() {
  const [stats] = useState<DashboardStats>(mockStats);
  const [chartData] = useState<ChartDataPoint[]>(mockChartData);
  const [activity] = useState<ActivityEvent[]>(mockActivity);

  const handleNewDispatch = useCallback(() => {
    // Navigate to copilot or open dispatch modal
  }, []);

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {statCards.map((card, index) => (
          <StatCard
            key={card.key}
            label={card.label}
            value={stats[card.key]}
            icon={card.icon}
            color={card.color}
            bg={card.bg}
            index={index}
          />
        ))}
      </div>

      {/* Chart + Activity */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="lg:col-span-2"
        >
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Dispatches - Ultimos 7 dias</CardTitle>
                <Badge variant="secondary" className="text-xs">
                  +23% vs semana anterior
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(239, 84%, 67%)" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="hsl(239, 84%, 67%)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="hsl(230, 15%, 16%)"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      stroke="hsl(215, 20%, 55%)"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      stroke="hsl(215, 20%, 55%)"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <RechartsTooltip content={<CustomTooltipContent />} />
                    <Area
                      type="monotone"
                      dataKey="dispatches"
                      stroke="hsl(239, 84%, 67%)"
                      strokeWidth={2}
                      fill="url(#chartGradient)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Activity Feed */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="h-full">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Atividade Recente</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {activity.map((event) => {
                  const config = activityIcons[event.type] ?? activityIcons["dispatch"]!;
                  const IconComponent = config.icon;
                  return (
                    <div key={event.id} className="flex gap-3">
                      <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-secondary ${config.color}`}>
                        <IconComponent className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground">
                          {event.title}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {event.description}
                        </p>
                        <p className="mt-0.5 text-[10px] text-muted-foreground/60">
                          {timeAgo(event.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Floating Action Button */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.6 }}
        className="fixed bottom-6 right-6 z-50 md:bottom-8 md:right-8"
      >
        <Button
          onClick={handleNewDispatch}
          size="lg"
          className="h-14 w-14 rounded-full shadow-xl shadow-primary/30 md:h-auto md:w-auto md:rounded-xl md:px-5"
          aria-label="Novo Dispatch"
        >
          <Plus className="h-6 w-6 md:h-5 md:w-5" />
          <span className="ml-2 hidden md:inline">Novo Dispatch</span>
        </Button>
      </motion.div>
    </div>
  );
}
